import { capture_supabase_error } from '$lib/utils/capture_supabase_error';
import { get_temp_answers } from './save_lesson_progress.js';

const has_answer_from_value = (value) =>
    value !== null && value !== undefined && `${value}`.trim().length > 0;

export function merge_temp_answers_into_course(course) {
    if (typeof window === 'undefined' || !localStorage) return course;

    course.lessons = course.lessons.map((lesson) => {
        if (Array.isArray(lesson.questions)) {
            const temp_answers = get_temp_answers(course.id, lesson.id);

            lesson.questions = lesson.questions.map((q) => {
                const temp_answer =
                    (q.question_id && temp_answers[q.question_id]) ||
                    (q.question_index !== undefined
                        ? temp_answers[q.question_index]
                        : undefined);
                const next = temp_answer ? { ...q, ...temp_answer } : q;
                return {
                    ...next,
                    has_answer: has_answer_from_value(next.user_answer)
                };
            });
        }

        return lesson;
    });

    return course;
}

/**
 * Patch the course user data
 * @param {Course} course - The course object
 * @param {import("@supabase/supabase-js").SupabaseClient} supabase - The supabase client
 * @param {string | null} user_id - The current user id
 * @param {import('@supabase/supabase-js').Session=} session - The current session
 * @param {{ teacher_access?: boolean; is_purchased?: boolean }} [options] - Access options
 * @returns {Promise<Course>} - The course object
 */
export async function patch_course_user_data(
    course,
    supabase,
    user_id,
    session = null,
    options = {}
) {
    const teacher_access = Boolean(options?.teacher_access);
    const is_purchased = Boolean(options?.is_purchased);
    course.purchased = is_purchased;
    course.has_teacher_access = teacher_access;

    if (!user_id) {
        return merge_temp_answers_into_course(course);
    }
    let res = [];

    try {
        const { data, error } = await supabase
            .from('Products')
            .select(
                `
                id,
                User_Video_Progress(video_id, progress, complete),
                User_Answers2(lesson_id, question_id, question_text, user_answer, is_correct),
                User_Reviews(review_text, review_rating, username, created_at),
                User_Lesson_Progress(lesson_id)
            `
            )
            .eq('id', course.id)
            .eq('type', 'course')
            .eq('User_Reviews.user_id', user_id)
            .eq('User_Video_Progress.user_id', user_id)
            .eq('User_Answers2.user_id', user_id);

        if (error) {
            console.error(error);
            capture_supabase_error(error, {
                message: 'Supabase error patching course user data',
                operation: 'select',
                table: 'Products'
            });
            return course;
        }

        res = data;
    } catch (error) {
        console.error('patch_course_user_data', error);
        capture_supabase_error(error, {
            message: 'Error patching course user data',
            operation: 'select',
            table: 'Products'
        });
        return course;
    }

    const course_row = res[0];
    if (!course_row) return course;

    course.videos = [];

    course.lessons = course.lessons.map((lesson, index) => {
        lesson.locked = !is_purchased && index + 1 > course.free_lessons;

        if (lesson.type === 'video' && lesson.videos.length) {
            course.videos.push(...lesson.videos);
        }

        if (Array.isArray(lesson.questions)) {
            const temp_answers = get_temp_answers(course.id, lesson.id);

            lesson.questions = lesson.questions.map((q) => {
                const question =
                    course_row.User_Answers2?.find(
                        (a) =>
                            a.lesson_id === lesson.id &&
                            a.question_id === q.question_id
                    ) || null;

                const temp_answer =
                    q.question_id !== undefined
                        ? temp_answers[q.question_id]
                        : undefined;

                if (question?.user_answer !== undefined) {
                    const next = {
                        ...q,
                        ...(question || {}),
                        user_answer: question.user_answer
                    };
                    return {
                        ...next,
                        has_answer: has_answer_from_value(next.user_answer)
                    };
                }

                if (temp_answer) {
                    const next = { ...q, ...temp_answer };
                    return {
                        ...next,
                        has_answer: has_answer_from_value(next.user_answer)
                    };
                }

                const next = { ...q, ...(question || {}) };
                return {
                    ...next,
                    has_answer: has_answer_from_value(next.user_answer)
                };
            });
        }

        lesson.complete = course_row.User_Lesson_Progress?.some(
            (p) => p.lesson_id === lesson.id
        );

        return lesson;
    });

    course.complete =
        course.lessons.length > 0 &&
        course.lessons.every((l) => l.complete);

    course.review = course_row.User_Reviews?.[0] || null;

    return course;
}

/**
 * Patch the worksheet user data
 * @param {Worksheet} worksheet - The worksheet object
 * @param {import("@supabase/supabase-js").SupabaseClient} supabase - The supabase client
 * @param {string | null} user_id - The current user id
 * @param {{ teacher_access?: boolean; is_purchased?: boolean }} [options] - Access options
 * @returns {Promise<Worksheet>} - The worksheet object
 */
export async function patch_worksheet_user_data(
    worksheet,
    supabase,
    user_id,
    options = {}
) {
    const teacher_access = Boolean(options?.teacher_access);
    const is_purchased = Boolean(options?.is_purchased);
    worksheet.purchased = is_purchased;
    worksheet.has_teacher_access = teacher_access;
    if (!user_id) {
        return worksheet;
    }
    let res = [];

    try {
        const { data, error } = await supabase
            .from('Products')
            .select(
                `
                id,
                User_Reviews(review_text, review_rating, username, created_at)
            `
            )
            .eq('id', worksheet.id)
            .eq('type', 'worksheet')
            .eq('User_Reviews.user_id', user_id);

        if (error) {
            console.error(error);
            capture_supabase_error(error, {
                message: 'Supabase error patching worksheet user data',
                operation: 'select',
                table: 'Products'
            });
            return worksheet;
        }

        res = data;
    } catch (error) {
        console.error('patch_worksheet_user_data', error);
        capture_supabase_error(error, {
            message: 'Error patching worksheet user data',
            operation: 'select',
            table: 'Products'
        });
        return worksheet;
    }

    const worksheet_row = res[0];
    if (!worksheet_row) return worksheet;

    if (worksheet_row.User_Reviews?.length) {
        const review = worksheet_row.User_Reviews[0];
        worksheet.review = {
            review_text: review.review_text,
            review_rating: review.review_rating,
            username: review.username
        };
    }

    if (worksheet.purchased) {
        worksheet.files = await get_worksheet_files(
            worksheet.text_id,
            supabase
        );
    }

    return worksheet;
}

/**
 * Get the worksheet files
 * @param {string} text_id - The worksheet text_id used for storage filenames
 * @param {import("@supabase/supabase-js").SupabaseClient} supabase - The supabase client
 * @returns {Promise<string[]>} - The signed urls
 */
export async function get_worksheet_files(text_id = '', supabase) {
    if (!text_id) return [];

    const filenames = [`${text_id}.zip`];
    let supabaseData;

    try {
        let { data, error } = await supabase.storage
            .from('sl-worksheets')
            .createSignedUrls(filenames, 60 * 60 * 24 * 7);

        if (error) {
            console.error(error);
            capture_supabase_error(error, {
                message: 'Supabase error getting worksheet files',
                operation: 'storage.createSignedUrls',
                table: 'sl-worksheets'
            });
            return [];
        }
        supabaseData = data;
    } catch (error) {
        console.error(error);
        capture_supabase_error(error, {
            message: 'Error getting worksheet files',
            operation: 'storage.createSignedUrls',
            table: 'sl-worksheets'
        });
        return [];
    }

    return supabaseData.map((record) => record.signedUrl).filter(Boolean);
}
